import React, { useMemo, useEffect, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { fetchMachines } from '../actions/machines'
import { fetchIncidences } from '../actions/incidences'
import { fetchSales } from '../actions/sales'

import ErrorMessage from '../components/ErrorMessage'
import LoadingProgress from '../components/LoadingProgress'

import { getMachineStatus } from '../utils/machines'
import { history } from '../store'

import '../styles/HomeView.css'

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts'

// ---------- helpers ----------

function formatLocalYMD(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function todayLocalYMD() {
  return formatLocalYMD(new Date())
}

const HOURS_24 = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

function computeHourlyRevenueFromSales(sales = []) {
  const byHour = new Map(HOURS_24.map(h => [h, 0]))
  for (const sale of sales) {
    const d = new Date(sale.TimeStamp || sale.timestamp || sale.createdAt || Date.now())
    const hour = `${String(d.getHours()).padStart(2, '0')}:00`
    const amount = Number(sale.Amount) || 0
    byHour.set(hour, (byHour.get(hour) || 0) + amount)
  }
  return HOURS_24.map(h => ({
    hour: h,
    revenue: Math.round((byHour.get(h) || 0) * 100) / 100
  }))
}

function getProductName(code) {
  switch (code) {
    case 'VWPACK':            return 'Pack'
    case 'VWLAY':             return 'Lay'
    case 'VWGO':              return 'Go'
    case 'LOCKERBASIC':       return 'Locker Basico'
    case 'LOCKERBASICXL':     return 'Locker Basico XL'
    case 'LOCKERCHARGER':     return 'Locker Cargador'
    case 'LOCKERCHARGERXL':   return 'Locker Cargador XL'
    case 'TUMBONAS':          return 'Tumbonas'
    case 'FLOTADORES':        return 'Flotadores'
    case 'FLOTADORESHALFDAY': return 'Flotadores HD'
    case 'FASTPASS':          return 'Fast Pass'
    default:                  return code || 'UNKNOWN'
  }
}

function renderDeltaRow(deltaPct, deltaAbs, invert, opts) {
  if (invert === undefined) invert = false
  if (opts === undefined) opts = {}
  if (deltaPct === null || deltaPct === undefined || deltaAbs === null || deltaAbs === undefined) {
    return null
  }
  const positive = deltaAbs > 0
  const color = positive
    ? (invert ? 'text-danger' : 'text-success')
    : deltaAbs < 0
      ? (invert ? 'text-success' : 'text-danger')
      : 'text-muted'
  const arrow = deltaAbs > 0 ? '▲' : deltaAbs < 0 ? '▼' : ''
  const pctDisplay = deltaPct !== null ? `${(deltaPct * 100).toFixed(1)}%` : '—'
  let absDisplay = ''
  if (opts.type === 'currency') {
    const absVal = Math.abs(deltaAbs)
    absDisplay = ` (${deltaAbs >= 0 ? '+' : '-'}€${absVal.toFixed(2)})`
  } else if (opts.type === 'percent') {
    const pts = (deltaAbs * 100).toFixed(1)
    absDisplay = ` (${deltaAbs >= 0 ? '+' : ''}${pts} pp)`
  } else {
    const num = Number(deltaAbs.toFixed(1))
    absDisplay = ` (${deltaAbs >= 0 ? '+' : ''}${num})`
  }
  return React.createElement(
    'div',
    { className: 'small text-muted', style: { marginTop: 2 } },
    React.createElement('span', null, 'vs LY: '),
    React.createElement('span', { className: color }, arrow + ' ' + pctDisplay + absDisplay)
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

function HomeView(props) {
  const { machines, incidences, auth } = props

  const [todaySalesLocal, setTodaySalesLocal] = useState([])
  const [lySalesLocal, setLySalesLocal]       = useState([])
  const [dataFetched, setDataFetched]         = useState(false)

  useEffect(() => {
    if (auth.role === 'PROMOTER') {
      history.push('/promoter/' + auth.id)
      return
    }

    const today = todayLocalYMD()
    const parts = today.split('-')
    const ly = (String(Number(parts[0]) - 1).padStart(4, '0')) + '-' + parts[1] + '-' + parts[2]

    props.fetchMachines()
    props.fetchIncidences('', '', 'OPEN', '', '', '')

    const p1 = props
      .fetchSales('', '', today, today, '', '', '')
      .then(function(act) {
        const arr = (act && act.response) || []
        setTodaySalesLocal(Array.isArray(arr) ? arr : [])
      })
    const p2 = props
      .fetchSales('', '', ly, ly, '', '', '')
      .then(function(act) {
        const arr = (act && act.response) || []
        setLySalesLocal(Array.isArray(arr) ? arr : [])
      })

    Promise.all([p1, p2]).finally(function() { setDataFetched(true) })
  }, []) // eslint-disable-line

  const allMachines = (machines && machines.machines) ? machines.machines : []
  const todaySales  = todaySalesLocal
  const lySales     = lySalesLocal

  // ── amounts ──
  const grossAmount = useMemo(function() {
    return todaySales.reduce(function(a, s) {
      if (Array.isArray(s.Items) && s.Items.length) {
        return a + s.Items.reduce(function(sum, it) { return sum + (Number(it.price) || 0) }, 0)
      }
      return a + Math.max(Number(s.Amount) || 0, 0)
    }, 0)
  }, [todaySales])

  const returnsAmount = useMemo(function() {
    return todaySales.reduce(function(a, s) { return a + Math.min(Number(s.Amount) || 0, 0) }, 0)
  }, [todaySales])

  const dayAmount = grossAmount + returnsAmount

  const lyGrossAmount = useMemo(function() {
    return lySales.reduce(function(a, s) {
      if (Array.isArray(s.Items) && s.Items.length) {
        return a + s.Items.reduce(function(sum, it) { return sum + (Number(it.price) || 0) }, 0)
      }
      return a + Math.max(Number(s.Amount) || 0, 0)
    }, 0)
  }, [lySales])

  const lyReturnsAmount = useMemo(function() {
    return lySales.reduce(function(a, s) { return a + Math.min(Number(s.Amount) || 0, 0) }, 0)
  }, [lySales])

  const lyDayAmount = lyGrossAmount + lyReturnsAmount

  // ── machine status counts ──
  const machineStatuses = useMemo(function() {
    const map = new Map([
      ['ONLINE', 0], ['OFFLINE', 0], ['SLEEP', 0],
      ['LOCKERS LLENOS', 0], ['SIN TICKET', 0], ['MANTENIMIENTO', 0],
      ['NIGHTSCAN', 0], ['SLEEP ONLINE', 0], ['SLEEP MAINTENANCE', 0], ['UNKNOWN', 0]
    ])
    for (const m of allMachines) {
      const s = getMachineStatus(m)
      map.set(s, (map.get(s) || 0) + 1)
    }
    return map
  }, [allMachines])

  const onlineCount =
    (machineStatuses.get('ONLINE') || 0) +
    (machineStatuses.get('SLEEP') || 0) +
    (machineStatuses.get('LOCKERS LLENOS') || 0) +
    (machineStatuses.get('SIN TICKET') || 0) +
    (machineStatuses.get('SLEEP ONLINE') || 0)

  const totalMachines = allMachines.length || 1

  // ── pct delta ──
  function pctDelta(cur, ly) {
    if (ly === 0 || ly == null) return null
    return (cur - ly) / (ly || 1)
  }

  // ── counts / averages ──
  const todayCount = todaySales.length
  const lyCount    = lySales.length
  const todayAvg   = todayCount ? (dayAmount / todayCount) : 0
  const lyAvg      = lyCount    ? (lyDayAmount / lyCount)  : 0

  const gmvDelta   = pctDelta(dayAmount, lyDayAmount)
  const gmvAbs     = dayAmount - lyDayAmount
  const countDelta = pctDelta(todayCount, lyCount)
  const avgDelta   = pctDelta(todayAvg, lyAvg)

  // ── avg operation time ──
  const avgSec = useMemo(function() {
    const times = todaySales.map(function(s) { return Number(s.OperationTime) || 0 })
    const sum   = times.reduce(function(a, b) { return a + b }, 0)
    return times.length ? Math.round(sum / times.length) : 0
  }, [todaySales])

  const lyAvgSec = useMemo(function() {
    const times = lySales.map(function(s) { return Number(s.OperationTime) || 0 })
    const sum   = times.reduce(function(a, b) { return a + b }, 0)
    return times.length ? Math.round(sum / times.length) : 0
  }, [lySales])

  const avgOpDelta = pctDelta(avgSec, lyAvgSec)
  const avgOpAbs   = avgSec - lyAvgSec

  const avgOpLabel = useMemo(function() {
    const mm = String(Math.floor(avgSec / 60)).padStart(2, '0')
    const ss = String(avgSec % 60).padStart(2, '0')
    return mm + ':' + ss
  }, [avgSec])

  // ── open incidences ──
  const openIncidences = (incidences && incidences.incidences) ? incidences.incidences.length : 0

  // ── donut colors ──
  const donutColors = [
    '#0b97ff', '#06b6d4', '#10b981', '#f59e0b',
    '#ec4899', '#8b5cf6', '#f97316', '#ef4444', '#6366f1'
  ]

  // ── payment method donut ──
  const paymentData = useMemo(function() {
    const map = new Map()
    for (const s of todaySales) {
      const pm = s.PaymentMethod || 'UNKNOWN'
      map.set(pm, (map.get(pm) || 0) + (Number(s.Amount) || 0))
    }
    const entries = Array.from(map.entries()).map(function(e) { return { name: e[0], value: Number(e[1]) || 0 } })
    const total = entries.reduce(function(a, d) { return a + d.value }, 0)
    if (total <= 0) return []
    return entries.filter(function(d) { return d.value > 0 }).map(function(d) { return Object.assign({}, d, { pct: (d.value / total) * 100 }) })
  }, [todaySales])

  const lyPaymentData = useMemo(function() {
    const map = new Map()
    for (const s of lySales) {
      const pm = s.PaymentMethod || 'UNKNOWN'
      map.set(pm, (map.get(pm) || 0) + (Number(s.Amount) || 0))
    }
    const entries = Array.from(map.entries()).map(function(e) { return { name: e[0], value: Number(e[1]) || 0 } })
    const total = entries.reduce(function(a, d) { return a + d.value }, 0)
    if (total <= 0) return []
    return entries.filter(function(d) { return d.value > 0 }).map(function(d) { return Object.assign({}, d, { pct: (d.value / total) * 100 }) })
  }, [lySales])

  const paymentLegend = useMemo(function() {
    return paymentData
      .map(function(d, i) {
        const lyEntry = lyPaymentData.find(function(x) { return x.name === d.name }) || { value: 0, pct: 0 }
        return Object.assign({}, d, { color: donutColors[i % donutColors.length], lyValue: lyEntry.value, lyPct: lyEntry.pct })
      })
      .sort(function(a, b) { return b.value - a.value })
  }, [paymentData, lyPaymentData])

  const lyPaymentLegend = useMemo(function() {
    return paymentLegend
      .map(function(d) { return { name: d.name, value: d.lyValue, pct: d.lyPct, color: d.color } })
      .filter(function(d) { return d.value > 0 })
  }, [paymentLegend])

  // ── product type donut ──
  const productTypeData = useMemo(function() {
    const map = new Map()
    for (const s of todaySales) {
      if (Array.isArray(s.Items) && s.Items.length) {
        for (const it of s.Items) {
          const name = getProductName(it.item)
          map.set(name, (map.get(name) || 0) + (it.qty || 1))
        }
      } else {
        const raw = s.ProductType || s.ItemType || 'UNKNOWN'
        const name = getProductName(raw)
        map.set(name, (map.get(name) || 0) + 1)
      }
    }
    const entries = Array.from(map.entries()).map(function(e) { return { name: e[0], value: e[1] } })
    const total = entries.reduce(function(a, d) { return a + d.value }, 0)
    if (total <= 0) return []
    return entries.map(function(d) { return Object.assign({}, d, { pct: (d.value / total) * 100 }) })
  }, [todaySales])

  const lyProductTypeData = useMemo(function() {
    const map = new Map()
    for (const s of lySales) {
      if (Array.isArray(s.Items) && s.Items.length) {
        for (const it of s.Items) {
          const name = getProductName(it.item)
          map.set(name, (map.get(name) || 0) + (it.qty || 1))
        }
      } else {
        const raw = s.ProductType || s.ItemType || 'UNKNOWN'
        const name = getProductName(raw)
        map.set(name, (map.get(name) || 0) + 1)
      }
    }
    const entries = Array.from(map.entries()).map(function(e) { return { name: e[0], value: e[1] } })
    const total = entries.reduce(function(a, d) { return a + d.value }, 0)
    if (total <= 0) return []
    return entries.map(function(d) { return Object.assign({}, d, { pct: (d.value / total) * 100 }) })
  }, [lySales])

  const productTypeLegend = useMemo(function() {
    return productTypeData
      .map(function(d, i) {
        const lyEntry = lyProductTypeData.find(function(x) { return x.name === d.name }) || { value: 0, pct: 0 }
        return Object.assign({}, d, { color: donutColors[i % donutColors.length], lyValue: lyEntry.value, lyPct: lyEntry.pct })
      })
      .sort(function(a, b) { return b.value - a.value })
  }, [productTypeData, lyProductTypeData])

  const lyProductTypeLegend = useMemo(function() {
    return productTypeLegend
      .map(function(d) { return { name: d.name, value: d.lyValue, pct: d.lyPct, color: d.color } })
      .filter(function(d) { return d.value > 0 })
  }, [productTypeLegend])

  const productTypeTotal   = useMemo(function() { return productTypeLegend.reduce(function(a, d) { return a + d.value }, 0) }, [productTypeLegend])
  const lyProductTypeTotal = useMemo(function() { return productTypeLegend.reduce(function(a, d) { return a + d.lyValue }, 0) }, [productTypeLegend])

  // ── machine id to label map ──
  const machineIdToLabel = useMemo(function() {
    const m = new Map()
    for (const mach of allMachines) {
      const label = mach.Serial || mach.Name || mach._id || ''
      if (mach._id)    m.set(String(mach._id), label)
      if (mach.Serial) m.set(String(mach.Serial), label)
    }
    return m
  }, [allMachines])

  // ── machine leaderboard ──
  const machineLeaderboard = useMemo(function() {
    const map = new Map()
    for (const s of todaySales) {
      let m = 'UNKNOWN'
      if (s.MachineName)    m = s.MachineName
      else if (s.Machine)   m = typeof s.Machine === 'string' ? s.Machine : (s.Machine.Serial || s.Machine._id || 'UNKNOWN')
      else if (s.MachineId) m = s.MachineId
      if (machineIdToLabel.has(m)) m = machineIdToLabel.get(m)
      const amt = Number(s.Amount) || 0
      const entry = map.get(m) || { total: 0, count: 0 }
      entry.total += amt
      entry.count += 1
      map.set(m, entry)
    }
    return Array.from(map.entries())
      .map(function(e) { return { name: e[0], total: e[1].total, count: e[1].count } })
      .sort(function(a, b) { return b.total - a.total })
      .slice(0, 5)
  }, [todaySales, machineIdToLabel])

  const machineLeaderboardLy = useMemo(function() {
    const map = new Map()
    for (const s of lySales) {
      let m = 'UNKNOWN'
      if (s.MachineName)    m = s.MachineName
      else if (s.Machine)   m = typeof s.Machine === 'string' ? s.Machine : (s.Machine.Serial || s.Machine._id || 'UNKNOWN')
      else if (s.MachineId) m = s.MachineId
      if (machineIdToLabel.has(m)) m = machineIdToLabel.get(m)
      const amt = Number(s.Amount) || 0
      const entry = map.get(m) || { total: 0, count: 0 }
      entry.total += amt
      entry.count += 1
      map.set(m, entry)
    }
    return Array.from(map.entries())
      .map(function(e) { return { name: e[0], total: e[1].total, count: e[1].count } })
      .sort(function(a, b) { return b.total - a.total })
      .slice(0, 5)
  }, [lySales, machineIdToLabel])

  const machineLeaderboardMerged = useMemo(function() {
    const lyMap = new Map(machineLeaderboardLy.map(function(x) { return [x.name, x] }))
    return machineLeaderboard.map(function(m) {
      const ly = lyMap.get(m.name) || { total: 0, count: 0 }
      return Object.assign({}, m, { lyTotal: ly.total, lyCount: ly.count })
    })
  }, [machineLeaderboard, machineLeaderboardLy])

  // ── hourly chart ──
  const hourlySeriesDay = useMemo(function() { return computeHourlyRevenueFromSales(todaySales) }, [todaySales])
  const peakHourDay = useMemo(function() {
    if (!hourlySeriesDay.length) return null
    return hourlySeriesDay.reduce(function(acc, p) {
      return p.revenue > ((acc && acc.revenue) != null ? acc.revenue : -Infinity) ? p : acc
    }, null).hour || null
  }, [hourlySeriesDay])

  const hourlySeriesLyDay = useMemo(function() { return computeHourlyRevenueFromSales(lySales) }, [lySales])
  const lyPeakHourDay = useMemo(function() {
    if (!hourlySeriesLyDay.length) return null
    return hourlySeriesLyDay.reduce(function(acc, p) {
      return p.revenue > ((acc && acc.revenue) != null ? acc.revenue : -Infinity) ? p : acc
    }, null).hour || null
  }, [hourlySeriesLyDay])

  const mergedHourly = useMemo(function() {
    const map = new Map()
    for (const p of hourlySeriesDay) {
      map.set(p.hour, { hour: p.hour, revenue: p.revenue, revenueLy: 0 })
    }
    for (const p of hourlySeriesLyDay) {
      const existing = map.get(p.hour)
      if (existing) existing.revenueLy = p.revenue
      else map.set(p.hour, { hour: p.hour, revenue: 0, revenueLy: p.revenue })
    }
    return Array.from(map.values()).sort(function(a, b) { return a.hour.localeCompare(b.hour) })
  }, [hourlySeriesDay, hourlySeriesLyDay])

  // ── fancy date ──
  const fancyDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  })

  // ─── early returns ───

  if (machines && machines.error)   return <ErrorMessage message={machines.error.message} />
  if (incidences && incidences.error) return <ErrorMessage message={incidences.error.message} />

  const isLoading =
    !machines || !machines.machines || machines.loading ||
    !incidences || !incidences.incidences || incidences.loading

  if (isLoading) {
    return <LoadingProgress />
  }

  // ─── render ───

  return (
    <div className="kpi-page">

      {/* Header */}
      <div className="kpi-page-header mb-3">
        <div className="kpi-title-section">
          <h1 className="kpi-title">
            <i className="fas fa-chart-line" />{' '}Dashboard del dia
          </h1>
          <div className="small text-muted mt-1">{fancyDate}</div>
        </div>
        {openIncidences > 0 && (
          <div className="d-flex align-items-center">
            <span className="badge badge-warning" style={{ fontSize: 14, padding: '6px 12px' }}>
              <i className="fas fa-exclamation-triangle mr-1" />
              {openIncidences} incidencia{openIncidences !== 1 ? 's' : ''} abierta{openIncidences !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {dataFetched && todayCount === 0 && lyCount === 0 && (
        <div className="alert alert-info">
          No hay ventas registradas para hoy ni para la misma fecha del ano pasado.
        </div>
      )}

      {/* Row 1: KPI cards */}
      <div className="kpi-row mb-3">

        <div className="kpi-card kpi-accent-total">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-server" /></div>
              <div className="kpi-card-title">Maquinas</div>
            </div>
            <div className="kpi-value">{allMachines.length}</div>
            <div className="small text-muted" style={{ marginTop: 2 }}>{onlineCount} online</div>
          </div>
        </div>

        <div className="kpi-card kpi-accent-occupancy">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-signal" /></div>
              <div className="kpi-card-title">Disponibilidad</div>
            </div>
            <div className="kpi-value">{totalMachines > 0 ? ((onlineCount / totalMachines) * 100).toFixed(0) : 0}%</div>
            <div className="small text-muted" style={{ marginTop: 2 }}>{onlineCount} / {allMachines.length} online</div>
          </div>
        </div>

        <div className="kpi-card kpi-accent-income-day">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-euro-sign" /></div>
              <div className="kpi-card-title">GMV total</div>
            </div>
            <div className="kpi-value">{dayAmount.toFixed(2)} EUR</div>
            {renderDeltaRow(gmvDelta, gmvAbs, false, { type: 'currency' })}
          </div>
        </div>

        <div className="kpi-card kpi-accent-income-day">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-shopping-cart" /></div>
              <div className="kpi-card-title">Transacciones</div>
            </div>
            <div className="kpi-value">{todayCount}</div>
            {renderDeltaRow(countDelta, todayCount - lyCount, false, { type: 'number' })}
          </div>
        </div>

        <div className="kpi-card kpi-accent-income-month">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-ticket-alt" /></div>
              <div className="kpi-card-title">Ticket med.</div>
            </div>
            <div className="kpi-value">{todayAvg.toFixed(2)} EUR</div>
            {renderDeltaRow(avgDelta, todayAvg - lyAvg, false, { type: 'currency' })}
          </div>
        </div>

        <div className="kpi-card kpi-accent-occupancy-month">
          <div className="kpi-card-inner">
            <div className="d-flex align-items-center gap-2">
              <div className="kpi-card-icon"><i className="fas fa-server" /></div>
              <div className="kpi-card-title">GMV x maquina</div>
            </div>
            <div className="kpi-value">{totalMachines > 0 ? (dayAmount / totalMachines).toFixed(2) : '0.00'} EUR</div>
            {renderDeltaRow(
              pctDelta(dayAmount / totalMachines, lyDayAmount / totalMachines),
              (dayAmount / totalMachines) - (lyDayAmount / totalMachines),
              false,
              { type: 'currency' }
            )}
          </div>
        </div>

      </div>

      {/* Row 2: chart + payment donut */}
      <div className="row g-3 align-items-stretch">

        <div className="col-12 col-lg-7 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <i className="fas fa-clock mr-2" />
                  <div className="kpi-section-title m-0">Ingreso por hora (hoy)</div>
                </div>
                {peakHourDay && (
                  <span className="kpi-peak">
                    Hora pico: <strong>{peakHourDay}</strong>
                    {lyPeakHourDay && <> / LY <strong>{lyPeakHourDay}</strong></>}
                  </span>
                )}
              </div>
              <div className="kpi-chart" style={{ flex: 1, display: 'flex' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mergedHourly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" ticks={HOURS_24} interval={0} tick={{ fontSize: 12 }} tickMargin={8} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={function(v) { return [v + ' EUR', 'Ingreso'] }} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue"   name="Ingreso"   stroke="#0b97ff" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="revenueLy" name="Ingreso LY" stroke="#ff9800" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-5 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100 kpi-bg-dark">
            <div className="card-body d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-credit-card mr-2" />
                <div className="kpi-section-title m-0">Metodo de pago</div>
              </div>
              {paymentLegend.length > 0 ? (
                <div className="kpi-donut-row mt-4">
                  <div className="kpi-donut-chart" style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={paymentLegend} dataKey="value" nameKey="name"
                          cx="50%" cy="50%" innerRadius={75} outerRadius={105} paddingAngle={2}
                          label={function(props) {
                            const entry = paymentLegend[props.index] || {}
                            return entry.pct ? (entry.pct.toFixed(1) + '%') : ''
                          }}
                          labelLine={{ stroke: '#6b7280', strokeDasharray: '3 4', strokeWidth: 1 }}
                        >
                          {paymentLegend.map(function(entry) {
                            return <Cell key={entry.name} fill={entry.color} />
                          })}
                        </Pie>
                        {lyPaymentLegend.length > 0 && (
                          <Pie
                            data={lyPaymentLegend} dataKey="value" nameKey="name"
                            cx="50%" cy="50%" innerRadius={50} outerRadius={70}
                            paddingAngle={1} label={false} labelLine={false}
                          >
                            {lyPaymentLegend.map(function(entry) {
                              return <Cell key={entry.name} fill={entry.color} fillOpacity={0.5} />
                            })}
                          </Pie>
                        )}
                        <Tooltip
                          formatter={function(value, name) {
                            return [
                              Number(value).toFixed(2) + ' EUR (' + (dayAmount > 0 ? ((value / dayAmount) * 100).toFixed(1) : 0) + '%)',
                              name
                            ]
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none', zIndex: 1, color: '#000000'
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{dayAmount.toFixed(2)} EUR</div>
                      {lyPaymentLegend.length > 0 && (
                        <div style={{ fontSize: 12, color: '#d1d5db', marginTop: 2 }}>
                          vs LY: {lyDayAmount.toFixed(2)} EUR
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="kpi-donut-legend">
                    <ul>
                      {paymentLegend.map(function(d) {
                        return (
                          <li key={d.name} className="kpi-legend-item">
                            <span className="d-flex align-items-center">
                              <svg className="mr-1" width="10" height="10" viewBox="0 0 10 10">
                                <circle cx="5" cy="5" r="5" fill={d.color} />
                              </svg>
                              <span className="kpi-legend-label">{d.name}</span>
                            </span>
                            <div className="kpi-legend-value">
                              <span>{d.pct.toFixed(1)}% - {d.value.toFixed(2)} EUR</span>
                              {d.lyValue > 0 && (
                                <span className="kpi-ly-info">vs LY: ({d.lyPct.toFixed(1)}%) - {d.lyValue.toFixed(2)} EUR</span>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="kpi-donut-empty">Sin datos de ventas</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: product type donut + machine leaderboard */}
      <div className="row g-3 align-items-stretch mt-3">

        <div className="col-12 col-lg-6 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100 kpi-bg-dark">
            <div className="card-body d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-layer-group mr-2" />
                <div className="kpi-section-title m-0">Tipos de producto</div>
              </div>
              {productTypeLegend.length > 0 ? (
                <div className="kpi-donut-row mt-4">
                  <div className="kpi-donut-chart" style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={productTypeLegend} dataKey="value" nameKey="name"
                          cx="50%" cy="50%" innerRadius={75} outerRadius={105} paddingAngle={2}
                          label={function(props) {
                            const entry = productTypeLegend[props.index] || {}
                            return entry.pct ? (entry.pct.toFixed(1) + '%') : ''
                          }}
                          labelLine={{ stroke: '#6b7280', strokeDasharray: '3 4', strokeWidth: 1 }}
                        >
                          {productTypeLegend.map(function(entry) {
                            return <Cell key={entry.name} fill={entry.color} />
                          })}
                        </Pie>
                        {lyProductTypeTotal > 0 && (
                          <Pie
                            data={productTypeLegend.map(function(d) { return { name: d.name, value: d.lyValue, color: d.color } })}
                            dataKey="value" nameKey="name"
                            cx="50%" cy="50%" innerRadius={50} outerRadius={70}
                            paddingAngle={1} label={false} labelLine={false}
                          >
                            {productTypeLegend.map(function(entry) {
                              return <Cell key={entry.name} fill={entry.color} fillOpacity={0.5} />
                            })}
                          </Pie>
                        )}
                        <Tooltip
                          formatter={function(value, name) {
                            return [
                              value + ' (' + (productTypeTotal > 0 ? ((value / productTypeTotal) * 100).toFixed(1) : 0) + '%)',
                              name
                            ]
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none', zIndex: 1, color: '#000000'
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{productTypeTotal}</div>
                      {lyProductTypeTotal > 0 && (
                        <div style={{ fontSize: 12, color: '#d1d5db', marginTop: 2 }}>vs LY: {lyProductTypeTotal}</div>
                      )}
                    </div>
                  </div>
                  <div className="kpi-donut-legend">
                    <ul>
                      {productTypeLegend.map(function(d) {
                        return (
                          <li key={d.name} className="kpi-legend-item">
                            <span className="d-flex align-items-center">
                              <svg className="mr-1" width="10" height="10" viewBox="0 0 10 10">
                                <circle cx="5" cy="5" r="5" fill={d.color} />
                              </svg>
                              <span className="kpi-legend-label">{d.name}</span>
                            </span>
                            <div className="kpi-legend-value">
                              <span>{d.pct.toFixed(1)}% - {d.value}</span>
                              {d.lyValue > 0 && (
                                <span className="kpi-ly-info">vs LY: ({d.lyPct.toFixed(1)}%) - {d.lyValue}</span>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="kpi-donut-empty">Sin datos</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <i className="fas fa-microchip mr-2" />
                <div className="kpi-section-title m-0">Top maquinas</div>
              </div>
              {machineLeaderboardMerged.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Maquina</th>
                        <th className="text-right">Transacciones</th>
                        <th className="text-right">Ventas</th>
                        <th className="text-right">Avg ticket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {machineLeaderboardMerged.map(function(m) {
                        return (
                          <tr key={m.name} className="modern-list-item">
                            <td><small className="text-muted">{m.name}</small></td>
                            <td className="text-right">
                              {m.count}
                              {m.lyCount != null && <div className="small text-muted">vs LY: {m.lyCount}</div>}
                            </td>
                            <td className="text-right">
                              {m.total.toFixed(2)} EUR
                              {m.lyTotal != null && <div className="small text-muted">vs LY: {m.lyTotal.toFixed(2)} EUR</div>}
                            </td>
                            <td className="text-right">
                              {(m.total / m.count).toFixed(2)} EUR
                              {m.lyCount
                                ? <div className="small text-muted">vs LY: {(m.lyTotal / (m.lyCount || 1)).toFixed(2)} EUR</div>
                                : null}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted small py-3 text-center">No hay datos de maquinas</div>
              )}

              {/* Machine status summary */}
              <div className="mt-3 pt-3 border-top">
                <div className="kpi-section-title mb-2">Estado de maquinas</div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {[
                    { label: 'Online',       key: 'ONLINE',          color: '#10b981' },
                    { label: 'Sleep',         key: 'SLEEP',           color: '#06b6d4' },
                    { label: 'Sleep Online',  key: 'SLEEP ONLINE',    color: '#06b6d4' },
                    { label: 'Llenas',        key: 'LOCKERS LLENOS',  color: '#f59e0b' },
                    { label: 'Sin ticket',    key: 'SIN TICKET',      color: '#f97316' },
                    { label: 'Mantenimiento', key: 'MANTENIMIENTO',   color: '#8b5cf6' },
                    { label: 'Offline',       key: 'OFFLINE',         color: '#ef4444' },
                    { label: 'Desconocida',   key: 'UNKNOWN',         color: '#9ca3af' },
                  ].map(function(item) {
                    const count = machineStatuses.get(item.key) || 0
                    if (count === 0) return null
                    return (
                      <span key={item.key} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 999,
                        fontSize: 12, fontWeight: 600,
                        background: item.color + '18', color: item.color, border: '1px solid ' + item.color + '40'
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                        {item.label}: {count}
                      </span>
                    )
                  })}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

const mapStateToProps = state => ({
  machines:   state.machines,
  incidences: state.incidences,
  sales:      state.sales,
  auth:       state.auth,
})

const mapDispatchToProps = dispatch => ({
  fetchMachines:   bindActionCreators(fetchMachines, dispatch),
  fetchIncidences: bindActionCreators(fetchIncidences, dispatch),
  fetchSales:      bindActionCreators(fetchSales, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(HomeView)
